/**
 * PATH: packages/email-hub/src/feature/lib/sortEmails.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SORT-001
 * PURPOSE: Pure client-side comparators for the inbox row order, plus the
 *          option catalogue the toolbar sort control renders from. ONE source
 *          of truth so the menu labels and the actual sort can never drift.
 * WHY:     Operator 2026-06-23 (with screenshot): "I tried to rearrange the
 *          mail order by click[ing] … and nothing." The inbox had NO sort
 *          control at all — order was hardcoded `received_at` descending (the
 *          backend `ordering=['-received_at']` default, mirrored client-side in
 *          dedup.ts). Clicking did nothing because nothing was wired. This adds
 *          a real, discoverable sort the operator can change (newest / oldest /
 *          unread-first / by sender) entirely on the already-loaded rows — no
 *          backend round-trip, so it applies instantly to what's on screen.
 * FLOW:    (deduped rows, mode) → stable comparator → reordered copy.
 * RELATED:
 *   - feature/lib/dedup.ts: produces the canonical deduped list this reorders.
 *   - feature/pages/InboxPage.tsx: owns the `sortMode` state + toolbar control
 *     and applies this inside the `dedupedEmails` memo (before mailbox grouping).
 */

/** The user-selectable inbox orderings. `newest` is the historical default. */
export type EmailSortMode = 'newest' | 'oldest' | 'unread' | 'sender'

export const DEFAULT_EMAIL_SORT: EmailSortMode = 'newest'

export interface EmailSortOption {
  id: EmailSortMode
  label: string
  /** One-line tooltip explaining the ordering. */
  hint: string
}

/** The toolbar menu renders straight from this — add an option here and it
 *  appears in the control with no other wiring. */
export const EMAIL_SORT_OPTIONS: EmailSortOption[] = [
  { id: 'newest', label: 'Newest first', hint: 'Most recently received at the top (default)' },
  { id: 'oldest', label: 'Oldest first', hint: 'Earliest received at the top' },
  { id: 'unread', label: 'Unread first', hint: 'Unread on top, then newest' },
  { id: 'sender', label: 'Sender A → Z', hint: 'Grouped alphabetically by sender' },
]

/** Narrow type guard so a persisted/raw value can be validated before use. */
export function isEmailSortMode(value: unknown): value is EmailSortMode {
  return (
    value === 'newest' ||
    value === 'oldest' ||
    value === 'unread' ||
    value === 'sender'
  )
}

/** The minimal row shape the comparators read — keeps `sortEmailsBy` generic
 *  over `Email` / `DedupedEmail` (and trivially testable). */
export interface EmailSortFields {
  received_at: string
  is_read?: boolean
  from_name?: string | null
  from_address?: string | null
}

/** received_at → epoch ms. Defensive: a missing/garbage timestamp coerces to 0
 *  (sorts to the bottom under newest-first) instead of producing NaN, which
 *  would make Array.sort's order unstable/undefined. */
function receivedMs(e: EmailSortFields): number {
  const t = new Date(e.received_at).getTime()
  return Number.isFinite(t) ? t : 0
}

/** Sender sort key — display name wins, else the address local-part, lowered
 *  and trimmed so "Mary"/"mary@…" cluster predictably. */
function senderKey(e: EmailSortFields): string {
  return (e.from_name || e.from_address || '').trim().toLowerCase()
}

/**
 * sortEmailsBy(rows, mode) → a NEW array ordered per `mode`.
 *
 * Pure: never mutates the input (callers pass a memoised list). Every branch
 * falls back to newest-first as the tie-breaker so equal keys stay in a stable,
 * predictable order rather than whatever the engine's sort happens to do.
 */
export function sortEmailsBy<T extends EmailSortFields>(
  rows: readonly T[],
  mode: EmailSortMode,
): T[] {
  const out = rows.slice()
  switch (mode) {
    case 'oldest':
      return out.sort((a, b) => receivedMs(a) - receivedMs(b))
    case 'unread':
      return out.sort((a, b) => {
        // Unread (is_read falsey) leads; newest within each band.
        const ua = a.is_read ? 1 : 0
        const ub = b.is_read ? 1 : 0
        if (ua !== ub) return ua - ub
        return receivedMs(b) - receivedMs(a)
      })
    case 'sender':
      return out.sort((a, b) => {
        const c = senderKey(a).localeCompare(senderKey(b))
        if (c !== 0) return c
        return receivedMs(b) - receivedMs(a)
      })
    case 'newest':
    default:
      return out.sort((a, b) => receivedMs(b) - receivedMs(a))
  }
}
